import { Icon } from '@iconify/react';
import { useState } from 'react';

/**
 * Botão de copiar o link de suporte, ao lado da identidade da conexão.
 *
 * Existe porque a coluna dizia "Formulário publicado" e não dava o link. Para
 * mandar o endereço a um cliente era preciso abrir o cadastro da conexão,
 * achar o campo e selecionar o texto à mão — três passos para a operação mais
 * comum desta tela.
 *
 * O que copia é o **`/c/<token>`**, o link de chat. Existe também um
 * `/f/<session_ref>`, do formulário antigo, que é só entrada: o cliente
 * escreve e não recebe resposta. Copiar o errado é fácil e o estrago aparece
 * tarde — do lado do cliente, esperando uma resposta que não chega.
 */
export function CopiarLink({ url, rotulo = 'Copiar link de suporte' }: { url: string; rotulo?: string }) {
  const [copiado, setCopiado] = useState(false);
  const [erro, setErro] = useState(false);

  const copiar = async (e: React.MouseEvent) => {
    // A linha da grade é clicável: sem isto, copiar também selecionaria a
    // conexão e abriria o painel lateral por cima do que a pessoa ia colar.
    e.stopPropagation();
    e.preventDefault();

    try {
      // `navigator.clipboard` exige contexto seguro **e** permissão. Dentro do
      // iframe do SGA Desk pode faltar a permissão mesmo em HTTPS, e o erro é
      // uma promessa rejeitada — sem o `catch` o botão não daria sinal nenhum.
      await navigator.clipboard.writeText(url);
      setCopiado(true);
      setErro(false);
      setTimeout(() => setCopiado(false), 1800);
    } catch {
      // Sem área de transferência, seleciona o texto para a pessoa copiar com
      // o teclado. Pior que um clique, muito melhor que um botão que não faz
      // nada e não explica.
      setErro(true);
      setTimeout(() => setErro(false), 4000);
    }
  };

  return (
    <>
      <button
        type="button"
        className="copiar-link"
        onClick={copiar}
        title={copiado ? 'Copiado' : `${rotulo}\n${url}`}
        aria-label={rotulo}
      >
        <Icon icon={copiado ? 'mdi:check' : 'mdi:content-copy'} width={14} />
        {copiado && <span className="copiar-link__ok">copiado</span>}
      </button>
      {erro && (
        <input
          className="copiar-link__saida"
          readOnly
          value={url}
          onFocus={(e) => e.currentTarget.select()}
          onClick={(e) => {
            e.stopPropagation();
            e.currentTarget.select();
          }}
          ref={(el) => el?.select()}
          aria-label="Link para copiar manualmente"
        />
      )}
    </>
  );
}
